import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportChart } from '../components/reports/report-chart.js';
import type { RangePickerValue } from '../components/reports/range-picker.js';
import { ReportShell, StatTile } from '../components/reports/report-shell.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { formatCurrency, formatNumber, formatPercent } from '../lib/format.js';
import { salesReportCsvPath } from '../lib/reports/reports-api.js';
import { useSalesReport } from '../lib/reports/use-reports.js';

/**
 * What the shop sold: the total, how it compares with the period before, which garments earned
 * it, and which customers brought it in.
 *
 * The comparison is against the same LENGTH of period immediately before, which the server
 * resolves — thirty days against thirty days, not this month against a month that happened to
 * have three more days in it. That is why the previous figures are read from the payload rather
 * than computed here from a second request.
 */
export function SalesReportPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [range, setRange] = useState<RangePickerValue>({ preset: 'last_30_days' });

  const allowed = role !== 'tailor';
  const report = useSalesReport(range, { enabled: allowed });
  const data = report.data;

  return (
    <ReportShell
      title={t('reports.sales.title')}
      description={t('reports.sales.description')}
      range={range}
      onRangeChange={setRange}
      resolved={data?.range}
      csvPath={salesReportCsvPath(range)}
      isPending={report.isPending && allowed}
      allowed={allowed}
    >
      {data ? (
        <>
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile
              label={t('reports.sales.revenue')}
              value={formatCurrency(data.totals.revenuePaise)}
              hint={t('reports.sales.versusPrevious', {
                amount: formatCurrency(Math.abs(data.change.revenuePaise)),
                direction: data.change.revenuePaise >= 0 ? t('reports.up') : t('reports.down'),
              })}
            />
            <StatTile
              label={t('reports.sales.orders')}
              value={formatNumber(data.totals.orderCount)}
              hint={t('reports.sales.itemCount', { count: data.totals.itemCount })}
            />
            <StatTile
              label={t('reports.sales.averageOrder')}
              value={formatCurrency(data.totals.averageOrderPaise)}
            />
            <StatTile
              label={t('reports.sales.discount')}
              value={formatCurrency(data.totals.discountPaise)}
            />
          </dl>

          <ReportChart
            title={t('reports.sales.byDay')}
            labelHeading={t('reports.day')}
            valueHeading={t('reports.sales.revenue')}
            points={data.byDay.map((row) => ({
              label: row.day,
              value: row.revenuePaise,
              display: formatCurrency(row.revenuePaise),
            }))}
          />

          <ReportChart
            title={t('reports.sales.byGarment')}
            labelHeading={t('reports.sales.garmentType')}
            valueHeading={t('reports.sales.revenue')}
            initialView="table"
            points={data.byGarmentType.map((row) => ({
              label: row.garmentTypeLabel,
              value: row.revenuePaise,
              display: `${formatCurrency(row.revenuePaise)} · ${formatPercent(row.share, 0)}`,
            }))}
          />

          {/* Beside the sales, never in them: an alteration is not new work sold, so its
              charge stays out of revenue and the average order. Absent when none were booked. */}
          {data.alterations ? (
            <section className="flex flex-col gap-2" aria-label={t('reports.sales.alterations')}>
              <h2 className="text-sm font-semibold">{t('reports.sales.alterations')}</h2>
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <StatTile
                  label={t('reports.sales.alterationCount')}
                  value={formatNumber(data.alterations.count)}
                  hint={t('reports.sales.alterationCharged', {
                    count: data.alterations.chargedCount,
                  })}
                />
                <StatTile
                  label={t('reports.sales.alterationCharges')}
                  value={formatCurrency(data.alterations.chargePaise)}
                />
                <StatTile
                  label={t('reports.sales.alterationCollected')}
                  value={formatCurrency(data.alterations.collectedPaise)}
                />
              </dl>
            </section>
          ) : null}

          <section className="flex flex-col gap-2" aria-label={t('reports.sales.topCustomers')}>
            <h2 className="text-sm font-semibold">{t('reports.sales.topCustomers')}</h2>
            {data.byCustomer.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.noData')}</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {data.byCustomer.map((row) => (
                  <li
                    key={row.customerId}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{row.customerName}</span>
                      <span className="block text-muted-foreground">
                        {t('reports.sales.orderCount', { count: row.orderCount })}
                      </span>
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(row.revenuePaise)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </ReportShell>
  );
}

export default SalesReportPage;
